/**
 * Item Component
 *
 * Flexible compound component for card-based layouts.
 * Inspired by shadcn UI's item component, adapted for SCSS styling.
 *
 * @example
 * <Item variant="default" size="default">
 *   <ItemMedia>{svg}</ItemMedia>
 *   <ItemLabel as="h4">Earn points</ItemLabel>
 *   <ItemMeta>+100 points</ItemMeta>
 *   <ItemActions>
 *     <Button>Claim</Button>
 *   </ItemActions>
 * </Item>
 */

import type React from 'react';
import { createContext, useContext, type ReactNode, type ElementType, type FC } from 'react';
import clsx from 'clsx';
import { Skeleton } from '../../skeleton';
import { ParagraphSkeleton } from '../../skeleton/patterns/paragraph';
import './item.scss';

// ============================================================================
// Types & Context
// ============================================================================

type ItemVariant = 'default' | 'disabled';
type ItemSize = 'default' | 'compact';

interface ItemContextValue {
	variant: ItemVariant;
	size: ItemSize;
}

const ItemContext = createContext<ItemContextValue | null>(null);

const useItemContext = () => {
	const context = useContext(ItemContext);
	if (!context) {
		throw new Error('Item compound components must be used within an Item component');
	}
	return context;
};

// ============================================================================
// Main Item Component
// ============================================================================

interface ItemProps {
	children: ReactNode;
	variant?: ItemVariant;
	size?: ItemSize;
	className?: string;
	style?: React.CSSProperties;
	onClick?: () => void;
}

export const Item: React.FC<ItemProps> = ({
	children,
	variant = 'default',
	size = 'default',
	className,
	style,
	onClick,
}) => {
	return (
		<ItemContext.Provider value={{ variant, size }}>
			<div
				className={clsx(
					'leat-item',
					`leat-item--${variant}`,
					`leat-item--${size}`,
					className
				)}
				style={style}
				onKeyDown={(e) => {
					if (onClick && (e.key === 'Enter' || e.key === ' ')) {
						e.preventDefault();
						onClick();
					}
				}}
				tabIndex={onClick ? 0 : undefined}
				onClick={onClick}
			>
				{children}
			</div>
		</ItemContext.Provider>
	);
};

// ============================================================================
// ItemMedia - Icon/Image Container
// ============================================================================

interface ItemMediaProps {
	children: ReactNode;
	className?: string;
}

export const ItemMedia: React.FC<ItemMediaProps> = ({ children, className }) => {
	return (
		<div className={clsx('leat-item__media', className)} data-slot="item-media">
			{children}
		</div>
	);
};

// ============================================================================
// ItemContent - Wrapper for text content
// ============================================================================

interface ItemContentProps {
	children: ReactNode;
	className?: string;
}

export const ItemContent: React.FC<ItemContentProps> = ({ children, className }) => {
	return (
		<div className={clsx('leat-item__content', className)} data-slot="item-content">
			{children}
		</div>
	);
};

// ============================================================================
// ItemLabel - Dynamic heading level support
// ============================================================================

interface ItemLabelProps {
	children: ReactNode;
	as?: ElementType;
	className?: string;
}

export const ItemLabel: React.FC<ItemLabelProps> = ({
	children,
	as: Component = 'h4',
	className,
}) => {
	return (
		<Component className={clsx('leat-item__label', className)} data-slot="item-label">
			{children}
		</Component>
	);
};

// ============================================================================
// ItemMeta - Credits/Cost/Status Display
// ============================================================================

interface ItemMetaProps {
	children: ReactNode;
	variant?: 'credits' | 'cost' | 'default' | 'coupon-code';
	className?: string;
	style?: React.CSSProperties;
}

export const ItemMeta: React.FC<ItemMetaProps> = ({
	children,
	variant = 'default',
	className,
	style,
}) => {
	return (
		<div
			className={clsx('leat-item__meta', `leat-item__meta--${variant}`, className)}
			data-slot="item-meta"
			style={style}
		>
			{children}
		</div>
	);
};

// ============================================================================
// ItemActions - Action buttons container
// ============================================================================

interface ItemActionsProps {
	children: ReactNode;
	className?: string;
}

export const ItemActions: React.FC<ItemActionsProps> = ({ children, className }) => {
	return (
		<div className={clsx('leat-item__actions', className)} data-slot="item-actions">
			{children}
		</div>
	);
};

// ============================================================================
// ItemError - Error message display
// ============================================================================

interface ItemErrorProps {
	children: ReactNode;
	className?: string;
}

export const ItemError: React.FC<ItemErrorProps> = ({ children, className }) => {
	return (
		<div className={clsx('leat-item__error', className)} data-slot="item-error">
			{children}
		</div>
	);
};

// ============================================================================
// ItemSuccess - Success message display
// ============================================================================

interface ItemSuccessProps {
	children: ReactNode;
	className?: string;
}

export const ItemSuccess: React.FC<ItemSuccessProps> = ({ children, className }) => {
	return (
		<div className={clsx('leat-item__success', className)} data-slot="item-success">
			{children}
		</div>
	);
};

// ============================================================================
// ItemDescription - Optional description text
// ============================================================================

interface ItemDescriptionProps {
	children: ReactNode;
	className?: string;
}

export const ItemDescription: React.FC<ItemDescriptionProps> = ({ children, className }) => {
	return (
		<div
			className={clsx('leat-item__description', className)}
			data-slot="item-description"
		>
			{children}
		</div>
	);
};

// ============================================================================
// ItemBadge - Badge for top right corner
// ============================================================================

interface ItemBadgeProps {
	children: ReactNode;
	className?: string;
	style?: React.CSSProperties;
}

export const ItemBadge: React.FC<ItemBadgeProps> = ({ children, className, style }) => {
	return (
		<div
			className={clsx('leat-item__badge', className)}
			data-slot="item-badge"
			style={style}
		>
			{children}
		</div>
	);
};

// ============================================================================
// Skeleton Components
// ============================================================================

export interface ItemSkeletonProps {
	/**
	 * Show the meta section (like credits or cost)
	 * @default true
	 */
	showMeta?: boolean;

	/**
	 * Custom aria message for screen readers
	 * @default "Loading..."
	 */
	ariaMessage?: string;

	/**
	 * Item variant for styling
	 * @default "default"
	 */
	variant?: 'default' | 'disabled';
}

/**
 * Item Skeleton Component
 *
 * A skeleton loader that uses the Item compound component structure
 * to provide a consistent loading state for card items.
 */
export const ItemSkeleton: FC<ItemSkeletonProps> = ({
	showMeta = true,
	ariaMessage = 'Loading...',
	variant = 'default',
}) => {
	return (
		<Item variant={variant}>
			<ItemContent>
				{/* Icon/Media skeleton */}
				<ItemMedia>
					<Skeleton width="48px" height="48px" borderRadius="50%" />
				</ItemMedia>

				{/* Label skeleton - 2 lines for multiline labels */}
				<ItemLabel as="div">
					<ParagraphSkeleton lines={2} ariaMessage={ariaMessage} />
				</ItemLabel>

				{/* Meta skeleton (credits, cost, etc.) */}
				{showMeta && (
					<ItemMeta variant="default">
						<Skeleton width="80px" height="20px" borderRadius="12px" />
					</ItemMeta>
				)}
			</ItemContent>

			{/* Action skeleton (button) */}
			<ItemActions>
				<Skeleton width="100%" height="36px" borderRadius="4px" />
			</ItemActions>
		</Item>
	);
};

export interface ItemGridSkeletonProps {
	/**
	 * Number of columns in the grid
	 * @default 3
	 */
	columns?: number;

	/**
	 * Number of skeleton cards to show
	 * @default Same as columns
	 */
	count?: number;

	/**
	 * Show meta section in skeleton cards
	 * @default true
	 */
	showMeta?: boolean;

	/**
	 * Custom aria message for screen readers
	 * @default "Loading..."
	 */
	ariaMessage?: string;

	/**
	 * Custom className for the grid
	 */
	className?: string;
}

/**
 * Item Grid Skeleton
 *
 * Renders a grid of ItemSkeleton components for loading states.
 * Use this in list blocks that display multiple Item cards.
 */
export const ItemGridSkeleton: FC<ItemGridSkeletonProps> = ({
	columns = 3,
	count,
	showMeta = true,
	ariaMessage = 'Loading...',
	className = '',
}) => {
	const skeletonCount = count ?? columns;

	return (
		<div
			className={className}
			style={{
				display: 'grid',
				gridTemplateColumns: `repeat(${columns}, 1fr)`,
				gap: '1rem',
			}}
			aria-live="polite"
			aria-label={ariaMessage}
		>
			{Array.from({ length: skeletonCount }).map((_, i) => (
				<ItemSkeleton key={i} showMeta={showMeta} ariaMessage={i === 0 ? ariaMessage : undefined} />
			))}
		</div>
	);
};

// ============================================================================
// Exports
// ============================================================================

export default Item;
